import { useEffect, useId, useRef } from 'react';

import type { InquiryError } from 'persona';

/**
 * Fixed, because nothing can size it to its content: `preferredDimensions` arrives empty for an
 * inline flow, `page-change`'s `pageHeight` reports a fraction of the rendered page, and the frame is
 * cross-origin so it cannot be measured from here. Detail and remaining options in ZRM-17119.
 */
const FRAME_HEIGHT = '800px';

/** An iframe with no width collapses to its ~300px intrinsic default. */
const FRAME_WIDTH = '100%';

const IFRAME_TITLE = 'Verify your identity';

export type PersonaInquiryProps = {
  inquiryId: string;
  sessionToken?: string;
  onComplete: () => void;
  onCancel: () => void;
  onError: (error: InquiryError | unknown) => void;
};

/**
 * Inline rather than in Persona's own modal, which rules out the `Client` class and leaves the
 * lower-level `setupIframe`/`setupEvents` pair. Wizard-agnostic so the mobile handoff page can use it.
 */
const PersonaInquiry = ({ inquiryId, sessionToken, onComplete, onCancel, onError }: PersonaInquiryProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // Identifies this iframe in the SDK's postMessage channel, so two mounts can't cross wires.
  const containerId = useId();

  // Held in a ref so a parent re-render can't tear down and re-create the iframe mid-flow.
  const callbacksRef = useRef({ onComplete, onCancel, onError });
  callbacksRef.current = { onComplete, onCancel, onError };

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let isCancelled = false;

    // Lazily: persona's UMD bundle reads `self` at module scope, which breaks the Next build.
    import('persona')
      .then(({ setupEvents, setupIframe }) => {
        if (isCancelled || !iframeRef.current) return;

        // Every callback must be present, null rather than undefined, per setupEvents' contract.
        unsubscribe = setupEvents(containerId, {
          onLoad: null,
          onReady: null,
          onEvent: null,
          onComplete: () => callbacksRef.current.onComplete(),
          onCancel: () => callbacksRef.current.onCancel(),
          onError: (error) => callbacksRef.current.onError(error),
          templateId: null,
          templateVersionId: null,
          host: null,
          eventsAllowlist: null,
        });

        setupIframe(iframeRef.current, containerId, 'inline', {
          inquiryId,
          sessionToken,
          iframeTitle: IFRAME_TITLE,
        });
        return undefined;
      })
      .catch((error: unknown) => {
        if (!isCancelled) callbacksRef.current.onError(error);
      });

    return () => {
      isCancelled = true;
      unsubscribe?.();
    };
  }, [inquiryId, sessionToken, containerId]);

  return (
    // setupIframe overwrites className, frameBorder, allow, sandbox, src and data-testid — it tags
    // the frame `persona-widget__iframe`, which is the handle to target from tests.
    <iframe ref={iframeRef} title={IFRAME_TITLE} style={{ border: 'none', width: FRAME_WIDTH, height: FRAME_HEIGHT }} />
  );
};

export default PersonaInquiry;
